<template>
  <div class="global-container">
    <div class="content">
    <!-- Header with Back Button -->
    <div class="header-section">
      <button class="back-btn" @click="goBack">
        <span class="back-icon">←</span>
      </button>
      <div class="header-content">
        <h1 class="title">Add Vendor</h1>
        <p class="subtitle">Create a new expense vendor</p>
      </div>
    </div>

    <!-- Form Section -->
    <div class="form-section">
      <div class="form-card">
        <h2 class="form-title">Vendor Details</h2>

        <!-- Vendor Name -->
        <div class="form-group">
          <label class="form-label">Vendor Name <span class="required">*</span></label>
          <input
            v-model="formData.name"
            type="text"
            placeholder="e.g., Amazon Web Services"
            class="form-input"
          >
        </div>

        <!-- Category -->
        <div class="form-group">
          <label class="form-label">Category <span class="required">*</span></label>
          <select v-model="formData.category" class="form-select">
            <option value="">Select category</option>
            <option
              v-for="cat in categories"
              :key="cat.id"
              :value="cat.name"
            >
              {{ cat.name }}
            </option>
          </select>
        </div>

        <!-- Email -->
        <div class="form-group">
          <label class="form-label">Email</label>
          <input
            v-model="formData.email"
            type="email"
            placeholder="billing@vendor.com"
            class="form-input"
          >
        </div>

        <!-- Phone and Website -->
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Phone (optional)</label>
            <input
              v-model="formData.phone"
              type="tel"
              placeholder="+1 555 123 4567"
              class="form-input"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Website (optional)</label>
            <input
              v-model="formData.website"
              type="url"
              placeholder="https://vendor.com"
              class="form-input"
            >
          </div>
        </div>

        <!-- Notes -->
        <div class="form-group">
          <label class="form-label">Notes (optional)</label>
          <textarea
            v-model="formData.notes"
            placeholder="Any additional notes about this vendor..."
            class="form-textarea"
          ></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <button class="cancel-btn" @click="goBack">Cancel</button>
          <button class="create-btn" @click="createVendor" :disabled="loading">
            {{ loading ? 'Creating...' : 'Create Vendor' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddVendor',
  data() {
    return {
      formData: {
        name: '',
        category: '',
        email: '',
        phone: '',
        website: '',
        notes: ''
      }
    }
  },
  computed: {
    ...mapGetters('vendor', ['loading']),
    ...mapGetters('category', { categories: 'categories' })
  },
  methods: {
    ...mapActions('vendor', { storeCreateVendor: 'createVendor' }),
    ...mapActions('category', ['fetchCategories']),

    goBack() {
      this.$router.push('/vendors')
    },
    async createVendor() {
      if (!this.formData.name || !this.formData.category) {
        this.$store.dispatch('notification/showError', 'Please fill in required fields (Vendor Name And Category)')
        return
      }

      try {
        const payload = {
          name: this.formData.name,
          email: this.formData.email,
          category: this.formData.category,
          phone: this.formData.phone || null,
          website: this.formData.website || null,
          notes: this.formData.notes || null
        }
        await this.storeCreateVendor(payload)
        this.goBack()
      } catch (error) {
        console.error('Create vendor failed:', error)
      }
    }
  },
  created() {
    this.fetchCategories()
  }
}
</script>

<style scoped>
.add-vendor-container {
  width: 100%;
  background: #f8fafc;
  margin-left: 280px;
  padding-top: 120px;
  min-height: 100vh;
}

/* Header */
.header-section {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 40px;
  background: #f8fafc;
}

.back-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #1f2937;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
  transition: color 0.2s ease;
}

.back-btn:hover {
  color: #6b7280;
}

.back-icon {
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 14px;
  color: #9ca3af;
  font-weight: 400;
  margin: 0;
}

/* Form */
.form-section {
  padding: 0 40px 48px 0px;
}

.form-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 32px;
  max-width: 540px;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  font-family: inherit;
  transition: border-color 0.2s ease;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: #9ca3af;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row .form-group {
  margin-bottom: 0;
}

/* Action Buttons */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn,
.create-btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.cancel-btn {
  background: #f3f4f6;
  color: #1f2937;
}

.cancel-btn:hover {
  background: #e5e7eb;
}

.create-btn {
  background: #2563eb;
  color: white;
}

.create-btn:hover {
  background: #1d4ed8;
}

.create-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .add-vendor-container {
    margin-left: 0;
    padding-top: 100px;
  }

  .header-section {
    padding: 32px 24px;
    gap: 12px;
  }

  .title {
    font-size: 24px;
  }

  .form-section {
    padding: 0 24px 32px 24px;
  }

  .form-card {
    padding: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .cancel-btn,
  .create-btn {
    width: 100%;
  }
}
</style>
